import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { ASSESSMENT_NAV, PRIMARY_NAV } from '../../lib/shell/navigation';
import { useDisclosure, useFocusTrap, useScrollLock } from '../../lib/shell/useDisclosure';
import { Button } from '../ui/button';
import { AccountAffordance } from './AccountAffordance';

/**
 * Mobile navigation for the Experience Shell (specification 3.2).
 *
 * Legacy has no distinct mobile navigation; the desktop arrangement is simply
 * narrower. This is a deliberate pattern instead:
 *
 * - **One level of interaction.** The assessments appear as a labelled group,
 *   not as a nested disclosure. Nesting a disclosure inside a panel is
 *   prohibited by the specification, because it makes the third destination two
 *   interactions deep on the smallest surface.
 * - Focus is trapped while open, Escape closes and restores focus to the
 *   toggle, selecting a destination closes the panel, and the page beneath does
 *   not scroll.
 */
export function MobileNav() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  /**
   * Close on any navigation, not only on the ones that come from inside the
   * panel.
   *
   * Destination links here call `close`, but the panel sits below the header
   * and leaves the brand mark tappable, and the brand mark is a bare link with
   * no close handler. A same-shell move such as `/privacy` -> `/` does not
   * remount this component, so the menu stayed open over a page the visitor had
   * already left, with the page scroll still locked. Browser Back and Forward
   * reach the same state. Cross-shell navigation happened to self-heal by
   * remounting, which only made the bug intermittent.
   *
   * Closing releases the scroll lock through `useScrollLock`'s cleanup.
   */
  useEffect(() => {
    setOpen(false);
  }, [location]);

  useDisclosure(open, close, panelRef, triggerRef);
  useFocusTrap(open, panelRef);
  useScrollLock(open);

  return (
    <div className="md:hidden">
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="sm"
        aria-expanded={open}
        aria-controls="mobile-nav-panel"
        data-testid="mobile-nav-trigger"
        onClick={() => setOpen((previous) => !previous)}
      >
        Menu
      </Button>

      {open ? (
        <div
          ref={panelRef}
          id="mobile-nav-panel"
          data-testid="mobile-nav-panel"
          className="absolute inset-x-0 top-full z-40 border-b border-border bg-popover px-4 py-6 text-popover-foreground shadow"
        >
          <nav aria-label="Primary" className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <p
                id="mobile-assessments-label"
                className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
              >
                Assessments
              </p>
              <ul aria-labelledby="mobile-assessments-label" className="flex flex-col gap-1">
                {ASSESSMENT_NAV.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={close}
                      className="block rounded-md px-3 py-2 text-sm transition-colors hover:bg-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <ul className="flex flex-col gap-1">
              {PRIMARY_NAV.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={close}
                    className="block rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* The account affordance lives inside the panel on mobile, visually
                separated, so the header row stays to the brand mark and one
                control. */}
            <div className="border-t border-border pt-4">
              <AccountAffordance idPrefix="mobile-account" layout="inline" />
            </div>
          </nav>
        </div>
      ) : null}
    </div>
  );
}
